// Boundary middleware intentionally uses Firebase SDK directly.
import firebase from 'firebase/app';
// Boundary middleware intentionally uses Firebase SDK directly.
import 'firebase/firestore';
import Firestore = firebase.firestore.Firestore;

export class FirestoreDbMiddlewareFactory {
	private readonly rawDb: Firestore;

	constructor(rawDb: Firestore) {
		this.rawDb = rawDb;
	}

	create (): Firestore {
		return new Proxy(this.rawDb, {
			get: (target, prop) => {
				if (prop === 'collection') {
					return (path: string) => this.wrapCollection(
						(target as Firestore).collection(path),
					);
				}
				// collectionGroup() returns a Query rather than a CollectionReference, so wrap it with
				// wrapQuery to keep its docs' `.ref` middleware-backed. Unwrapped, a cross-collection
				// scan hands back raw refs whose writes would skip _updated / the sqlSync delete record.
				if (prop === 'collectionGroup') {
					return (collectionId: string) => this.wrapQuery(
						(target as Firestore).collectionGroup(collectionId),
					);
				}
				if (prop === 'doc') {
					return (path: string) => this.wrapDoc(
						(target as Firestore).doc(path),
					);
				}
				if (prop === 'runTransaction') {
					return (updateFunction: (transaction: firebase.firestore.Transaction) => Promise<any>) => (
						(target as Firestore).runTransaction(
							(transaction: firebase.firestore.Transaction) => updateFunction(
								this.wrapTransaction(transaction),
							),
						)
					);
				}
				if (prop === 'batch') {
					return () => this.wrapWriteBatch(
						(target as Firestore).batch(),
					);
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as Firestore;
	}

	private withCreatedUpdated<T extends firebase.firestore.DocumentData> (data: T): T {
		return {
			...data,
			_created: firebase.firestore.FieldValue.serverTimestamp(),
			_updated: firebase.firestore.FieldValue.serverTimestamp(),
		};
	}

	private withUpdated<T extends firebase.firestore.UpdateData> (data: T): T {
		return {
			...data,
			_updated: firebase.firestore.FieldValue.serverTimestamp(),
		};
	}

	/**
	 * SQL sync checkpoints on top-level `_updated`. Varargs `update()` skips `withUpdated()`, so
	 * append `_updated` unless the caller already included it in this call (avoids duplicate-field errors).
	 */
	private static augmentFieldPathVarargsWithSqlSyncTimestamp(args: any[]): any[] {
		const updatedPath = new firebase.firestore.FieldPath('_updated');
		for (let i = 0; i < args.length; i += 2) {
			const k = args[i];
			if (k === '_updated') return args;
			if (k instanceof firebase.firestore.FieldPath) {
				try {
					if (k.isEqual(updatedPath)) return args;
				} catch {
					// ignore
				}
			}
		}
		return [...args, '_updated', firebase.firestore.FieldValue.serverTimestamp()];
	}

	/**
	 * True when SetOptions tells firestore to merge into an existing doc.
	 * In that case we must NOT stamp `_created` (the doc may already exist and have an authoritative original timestamp).
	 * Callers who want `_created` set on a known-new doc should use full-replace `.set(data)` without options.
	 */
	private static isUpsertMerge (options: firebase.firestore.SetOptions | undefined): boolean {
		if (!options) return false;
		if (options.merge === true) return true;
		if (options.mergeFields && options.mergeFields.length > 0) return true;
		return false;
	}

	private static readonly SQL_SYNC_SKIP_COLLECTIONS = new Set([
		'sqlSyncs',
		'queueCronTasks',
		'runnitNodeRunDrafts',
		'unprocessedBalanceRecords',
		'domainOwnership',
		'resultFavorites',
		// Boards v2 comments (IL-092): not mirrored to Postgres, and a delete's
		// sqlSyncs record would be refused by the rules
		'runnitCanvasThreads',
		'runnitCanvasComments',
		// The Boards v2 alpha's held lists (IL-114): the same
		'boardsV2AlphaHolds',
	]);

	// All Firestore Query methods that return a new Query and must be re-wrapped so that
	// the resulting Query's .get() still goes through this middleware. If the firebase
	// package is upgraded, verify this list against the new SDK's Query interface.
	private static readonly QUERY_BUILDER_METHODS = new Set([
		'where',          // filter
		'orderBy',        // sort
		'limit',          // cap results from start
		'limitToLast',    // cap results from end
		'startAt',        // cursor (inclusive)
		'startAfter',     // cursor (exclusive)
		'endAt',          // cursor (inclusive)
		'endBefore',      // cursor (exclusive)
		'offset',         // skip first N results (admin SDK only)
		'select',         // field mask projection (admin SDK only)
		'withConverter',  // type converter — returns a new typed Query<U>
	]);

	private shouldCreateDeleteSqlSync (docRef: firebase.firestore.DocumentReference): boolean {
		return !FirestoreDbMiddlewareFactory.SQL_SYNC_SKIP_COLLECTIONS.has(docRef.parent.id);
	}

	private buildDeleteSqlSyncData (docRef: firebase.firestore.DocumentReference): firebase.firestore.DocumentData {
		const collectionName: string = docRef.parent.id;
		const refPath: string = docRef.path;
		return {
			_created: firebase.firestore.FieldValue.serverTimestamp(),
			_updated: firebase.firestore.FieldValue.serverTimestamp(),
			type: 'DELETE',
			retryCount: 0,
			collectionName,
			docRef: this.rawDb.doc(refPath),
		};
	}

	private wrapDoc<T = firebase.firestore.DocumentData> (
		docRef: firebase.firestore.DocumentReference<T>,
	): firebase.firestore.DocumentReference<T> {
		return new Proxy(docRef, {
			get: (target, prop) => {
				if (prop === 'set') {
					return (data: T, options?: firebase.firestore.SetOptions) => {
						// On merge upsert, do NOT touch `_created` — the doc may already exist.
						// Only full-replace `.set(data)` resets `_created` (callers who do that are intentionally replacing the doc).
						const augmented = FirestoreDbMiddlewareFactory.isUpsertMerge(options)
							? { ...data, _updated: firebase.firestore.FieldValue.serverTimestamp() }
							: this.withCreatedUpdated(data as T);
						return (target as firebase.firestore.DocumentReference<T>).set(
							augmented as T,
							options,
						);
					};
				}
				if (prop === 'update') {
					return (...args: any[]) => {
						const first = args[0];
						const usesFieldPathVarargs = typeof first === 'string' || first instanceof firebase.firestore.FieldPath;
						if (usesFieldPathVarargs) {
							const augmented = FirestoreDbMiddlewareFactory.augmentFieldPathVarargsWithSqlSyncTimestamp(args);
							return (target as any).update(...augmented);
						}
						const valid: boolean = args.length >= 1 && args.length <= 2
							&& typeof args[0] === 'object' && !Array.isArray(args[0])
							&& (args.length === 1 || typeof args[1] === 'object');
						if (valid) {
							return (target as any).update(this.withUpdated(args[0]), ...args.slice(1));
						}
						throw new Error('update() with field/value pairs is not supported. Use update({ field: value }) or update(data, precondition) instead.');
					};
				}
				if (prop === 'collection') {
					return (path: string) => this.wrapCollection(
						(target as firebase.firestore.DocumentReference<T>).collection(path),
					);
				}
				if (prop === 'get') {
					return () => (target as firebase.firestore.DocumentReference<T>).get().then(
						(snapshot) => this.wrapDocSnapshot(snapshot),
					);
				}
				if (prop === 'delete') {
					return async () => {
						if (this.shouldCreateDeleteSqlSync(target as firebase.firestore.DocumentReference<T>)) {
							const sqlSyncRef: firebase.firestore.DocumentReference = this.rawDb.collection('sqlSyncs').doc();
							await sqlSyncRef.set(this.buildDeleteSqlSyncData(target as firebase.firestore.DocumentReference<T>));
						}
						await (target as firebase.firestore.DocumentReference<T>).delete();
					};
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.DocumentReference<T>;
	}

	private wrapCollection<T = firebase.firestore.DocumentData> (
		collectionRef: firebase.firestore.CollectionReference<T>,
	): firebase.firestore.CollectionReference<T> {
		return new Proxy(collectionRef, {
			get: (target, prop) => {
				if (prop === 'add') {
					return (data: T) => (
						(target as firebase.firestore.CollectionReference<T>).add(
							this.withCreatedUpdated(data as T),
						)
					);
				}
				if (prop === 'doc') {
					return (...args: any[]) => this.wrapDoc(
						(target as firebase.firestore.CollectionReference<T>).doc(...args),
					);
				}
				if (prop === 'get') {
					return () => (target as firebase.firestore.CollectionReference<T>).get().then(
						(snapshot) => this.wrapQuerySnapshot(snapshot),
					);
				}
				if (FirestoreDbMiddlewareFactory.QUERY_BUILDER_METHODS.has(prop as string)) {
					return (...args: any[]) => this.wrapQuery(
						((target as any)[prop] as Function).apply(target, args),
					);
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.CollectionReference<T>;
	}

	private wrapTransaction (
		transaction: firebase.firestore.Transaction,
	): firebase.firestore.Transaction {
		return new Proxy(transaction, {
			get: (target, prop) => {
				if (prop === 'set') {
					return (docRef: firebase.firestore.DocumentReference, data: any, options?: firebase.firestore.SetOptions) => {
						const augmented = FirestoreDbMiddlewareFactory.isUpsertMerge(options)
							? this.withUpdated(data)
							: this.withCreatedUpdated(data);
						return (target as firebase.firestore.Transaction).set(
							docRef,
							augmented,
							options,
						);
					};
				}
				if (prop === 'update') {
					return (docRef: firebase.firestore.DocumentReference, ...args: any[]) => {
						const first = args[0];
						const usesFieldPathVarargs = typeof first === 'string' || first instanceof firebase.firestore.FieldPath;
						if (usesFieldPathVarargs) {
							const augmented = FirestoreDbMiddlewareFactory.augmentFieldPathVarargsWithSqlSyncTimestamp(args);
							return (target as any).update(docRef, ...augmented);
						}
						const valid: boolean = args.length >= 1 && args.length <= 2
							&& typeof args[0] === 'object' && !Array.isArray(args[0])
							&& (args.length === 1 || typeof args[1] === 'object');
						if (valid) {
							return (target as any).update(docRef, this.withUpdated(args[0]), ...args.slice(1));
						}
						throw new Error('update() with field/value pairs is not supported. Use update(docRef, { field: value }) or update(docRef, data, precondition) instead.');
					};
				}
				if (prop === 'delete') {
					return (docRef: firebase.firestore.DocumentReference) => {
						if (this.shouldCreateDeleteSqlSync(docRef)) {
							const sqlSyncRef: firebase.firestore.DocumentReference = this.rawDb.collection('sqlSyncs').doc();
							(target as firebase.firestore.Transaction).set(
								sqlSyncRef,
								this.buildDeleteSqlSyncData(docRef),
							);
						}
						return (target as firebase.firestore.Transaction).delete(docRef);
					};
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.Transaction;
	}

	private wrapWriteBatch (
		batch: firebase.firestore.WriteBatch,
	): firebase.firestore.WriteBatch {
		return new Proxy(batch, {
			get: (target, prop) => {
				if (prop === 'set') {
					return (docRef: firebase.firestore.DocumentReference, data: any, options?: firebase.firestore.SetOptions) => {
						const augmented = FirestoreDbMiddlewareFactory.isUpsertMerge(options)
							? this.withUpdated(data)
							: this.withCreatedUpdated(data);
						return (target as firebase.firestore.WriteBatch).set(
							docRef,
							augmented,
							options,
						);
					};
				}
				if (prop === 'update') {
					return (docRef: firebase.firestore.DocumentReference, ...args: any[]) => {
						const first = args[0];
						const usesFieldPathVarargs = typeof first === 'string' || first instanceof firebase.firestore.FieldPath;
						if (usesFieldPathVarargs) {
							const augmented = FirestoreDbMiddlewareFactory.augmentFieldPathVarargsWithSqlSyncTimestamp(args);
							return (target as any).update(docRef, ...augmented);
						}
						const valid: boolean = args.length >= 1 && args.length <= 2
							&& typeof args[0] === 'object' && !Array.isArray(args[0])
							&& (args.length === 1 || typeof args[1] === 'object');
						if (valid) {
							return (target as any).update(docRef, this.withUpdated(args[0]), ...args.slice(1));
						}
						throw new Error('update() with field/value pairs is not supported. Use update(docRef, { field: value }) or update(docRef, data, precondition) instead.');
					};
				}
				if (prop === 'delete') {
					return (docRef: firebase.firestore.DocumentReference) => {
						if (this.shouldCreateDeleteSqlSync(docRef)) {
							const sqlSyncRef: firebase.firestore.DocumentReference = this.rawDb.collection('sqlSyncs').doc();
							(target as firebase.firestore.WriteBatch).set(
								sqlSyncRef,
								this.buildDeleteSqlSyncData(docRef),
							);
						}
						return (target as firebase.firestore.WriteBatch).delete(docRef);
					};
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.WriteBatch;
	}

	private wrapQuery<T = firebase.firestore.DocumentData> (
		query: firebase.firestore.Query<T>,
	): firebase.firestore.Query<T> {
		return new Proxy(query, {
			get: (target, prop) => {
				if (prop === 'get') {
					return () => (target as firebase.firestore.Query<T>).get().then(
						(snapshot) => this.wrapQuerySnapshot(snapshot),
					);
				}
				if (FirestoreDbMiddlewareFactory.QUERY_BUILDER_METHODS.has(prop as string)) {
					return (...args: any[]) => this.wrapQuery(
						((target as any)[prop] as Function).apply(target, args),
					);
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.Query<T>;
	}

	private wrapQuerySnapshot<T = firebase.firestore.DocumentData> (
		querySnapshot: firebase.firestore.QuerySnapshot<T>,
	): firebase.firestore.QuerySnapshot<T> {
		return new Proxy(querySnapshot, {
			get: (target, prop) => {
				if (prop === 'docs') {
					return target.docs.map((doc) => this.wrapQueryDocSnapshot(doc));
				}
				if (prop === 'forEach') {
					return (
						callback: (result: firebase.firestore.QueryDocumentSnapshot<T>) => void,
						thisArg?: any,
					) => {
						target.forEach(
							(doc) => callback(this.wrapQueryDocSnapshot(doc)),
							thisArg,
						);
					};
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.QuerySnapshot<T>;
	}

	private wrapQueryDocSnapshot<T = firebase.firestore.DocumentData> (
		docSnapshot: firebase.firestore.QueryDocumentSnapshot<T>,
	): firebase.firestore.QueryDocumentSnapshot<T> {
		return new Proxy(docSnapshot, {
			get: (target, prop) => {
				if (prop === 'ref') {
					return this.wrapDoc(target.ref);
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.QueryDocumentSnapshot<T>;
	}

	private wrapDocSnapshot<T = firebase.firestore.DocumentData> (
		docSnapshot: firebase.firestore.DocumentSnapshot<T>,
	): firebase.firestore.DocumentSnapshot<T> {
		return new Proxy(docSnapshot, {
			get: (target, prop) => {
				if (prop === 'ref') {
					return this.wrapDoc(target.ref);
				}
				const value = (target as any)[prop];
				return typeof value === 'function' ? value.bind(target) : value;
			},
		}) as firebase.firestore.DocumentSnapshot<T>;
	}
}
